/**
 * MyCopro - Admin Store
 * Gestion des données admin : résidences, syndics, utilisateurs, statistiques
 * ✅ CORRIGÉ: createdBy ne peut plus être undefined (Firestore n'accepte pas undefined)
 * ✅ NOUVEAU: Gestion complète des abonnements (CRUD, stats, export)
 * ✅ CORRIGÉ: Support du statut 'grace' dans tous les types et méthodes
 */

import { create } from 'zustand';
import { 
  collection, 
  query, 
  getDocs, 
  getDoc,
  getDocFromServer,
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  where,
  orderBy,
  Timestamp,
  writeBatch,
  deleteField
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '@/config/firebase';
import { createAllChatGroups } from '@/services/api/chat';
import {
  createDefaultResidence,
  generateResidenceCode,
  CreateResidenceData
} from '@/types/residence';
import { generateUniqueResidenceCode } from '@/utils/residenceCode';
import { logger } from '@/utils/logger';
import { batchFetchMemberPrivate } from '@/utils/memberPrivate';

// ✅ Initialiser functions avec la région europe-west1
const functions = getFunctions(undefined, 'europe-west1');

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Supprime les champs undefined d'un objet (Firestore les refuse)
 */
function removeUndefinedFields<T extends Record<string, any>>(obj: T): Partial<T> {
  const result: Partial<T> = {};
  for (const key in obj) {
    if (obj[key] !== undefined) {
      result[key] = obj[key];
    }
  }
  return result;
}

/**
 * Normalise les données syndic embarquées dans une résidence.
 * Gère la compatibilité entre l'ancien format (champ "name") et le nouveau (champ "companyName").
 */
function normalizeSyndicData(
  syndicData: Record<string, any> | null | undefined
): AdminResidence['syndic'] {
  if (!syndicData) return null;
  return {
    companyName: syndicData.companyName || syndicData.name || '',
    contactName: syndicData.contactName || '',
    email: syndicData.email || '',
    phone: syndicData.phone || ''
  };
}

// ============================================================================
// TYPES
// ============================================================================

export interface Syndic {
  id: string;
  companyName: string;
  address: string;
  phone: string;
  email: string;
  portalUrl?: string;  // ✅ URL portail copropriétaire
  status: 'pending' | 'active' | 'suspended' | 'revoked';
  managedResidenceIds: string[];
  stats: {
    totalResidences: number;
    totalLots: number;
    pendingRequests: number;
  };
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  // Champs legacy (pour compatibilité avec anciennes données)
  siret?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
}

export interface SyndicMandate {
  id: string;
  syndicId: string;
  residenceId: string;
  startDate: string;
  endDate: string | null;
  terminatedAt: string | null;
  status: 'active' | 'expired' | 'terminated' | 'transferred';
  terminationReason: 'contract_end' | 'revoked' | 'transferred' | null;
  transferredToSyndicId: string | null;
  transferredFromSyndicId: string | null;
  createdAt: string;
  createdBy: string;
}

// ✅ CORRIGÉ: Statuts alignés avec Cloud Functions (inclut 'grace')
export type AdminSubscriptionStatus = 'active' | 'expiring' | 'expired' | 'trial' | 'grace';

export interface AdminResidence {
  id: string;
  name: string;
  address: string;
  postalCode: string;
  city: string;
  residenceCode: string;
  syndicId: string | null;
  syndic: {
    companyName: string;
    email: string;
    phone: string;
    contactName: string;
  } | null;
  stats: {
    apartments: number;
    owners: number;
    tenants: number;
  };
  membersCount: number;
  pendingRequestsCount: number;
  photo?: string;
  createdAt: string;
  planAlert?: {
    activeUsers: number;
    plan: 'small' | 'medium' | 'large';
    detectedAt: string;
    lastNotifiedAt: string;
  } | null;
  activeUsersCount?: number | null;
  // Résidence pré-créée par un admin, essai différé jusqu'au premier vrai membre
  preSeeded?: boolean;
  // ✅ CORRIGÉ: Abonnement avec support 'grace' et graceStartDate
  subscription?: {
    status: AdminSubscriptionStatus;
    startDate: string;
    endDate: string;
    plan?: 'small' | 'medium' | 'large' | 'xlarge' | null;
    autoRenew?: boolean;
    graceStartDate?: string;       // ✅ Date début période de grâce
    statusUpdatedAt?: string;      // ✅ Dernière mise à jour du statut
    lastReminderSent?: string;     // ✅ Dernier rappel envoyé
  };
}

// ✅ CORRIGÉ: Interface pour la gestion des abonnements (inclut 'grace')
export interface SubscriptionData {
  status: AdminSubscriptionStatus;
  startDate: string;
  endDate: string;
  plan: 'small' | 'medium' | 'large' | 'xlarge';
  autoRenew: boolean;
}

export interface SubscriptionUpdate {
  status?: AdminSubscriptionStatus;
  endDate?: string;
  plan?: 'small' | 'medium' | 'large';
  autoRenew?: boolean;
}

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  category: string;
  residenceId: string | null;
  residenceName?: string;
  apartment?: string;
  status: 'pending' | 'active' | 'rejected';
  enabled: boolean;
  syndicId?: string;
  syndicName?: string;
  createdAt: string;
}

export interface AdminStats {
  totalResidences: number;
  totalSyndics: number;
  totalMembers: number;
  totalLots: number;
  pendingRequests: number;
  pendingInscriptions: number;
  newMembersThisMonth: number;
}

// Type pour la création de syndic via Cloud Function (simplifié)
export interface CreateSyndicData {
  companyName: string;
  address?: string;
  email: string;
  phone?: string;
  portalUrl?: string;
}

// ============================================================================
// STORE
// ============================================================================

interface AdminState {
  // Données
  residences: AdminResidence[];
  syndics: Syndic[];
  users: AdminUser[];
  stats: AdminStats | null;

  // Chargement
  isLoadingResidences: boolean;
  isLoadingSyndics: boolean;
  isLoadingUsers: boolean;
  isLoadingStats: boolean;

  // Erreurs
  error: string | null;

  // Actions - Statistiques
  loadStats: () => Promise<void>;

  // Actions - Résidences
  loadAllResidences: () => Promise<void>;
  getResidenceById: (id: string) => Promise<AdminResidence | null>;
  createResidence: (data: CreateResidenceData & { stats?: { apartments: number; owners: number; tenants: number } }) => Promise<string | null>;
  updateResidence: (id: string, data: Partial<AdminResidence>) => Promise<boolean>;
  deleteResidence: (id: string) => Promise<boolean>;

  // Actions - Syndics
  loadAllSyndics: () => Promise<void>;
  getSyndicById: (id: string) => Promise<Syndic | null>;
  createSyndic: (data: CreateSyndicData, adminId: string) => Promise<string | null>;
  updateSyndic: (id: string, data: Partial<Syndic>) => Promise<boolean>;
  suspendSyndic: (id: string) => Promise<boolean>;
  revokeSyndic: (id: string) => Promise<boolean>;
  activateSyndic: (id: string) => Promise<boolean>;

  // Actions - Mandats
  assignResidenceToSyndic: (residenceId: string, syndicId: string, startDate: string, endDate: string | null, adminId: string) => Promise<boolean>;
  terminateMandate: (residenceId: string, reason: string, adminId: string) => Promise<boolean>;
  transferResidence: (residenceId: string, toSyndicId: string, transferDate: string, reason: string, adminId: string) => Promise<boolean>;

  // Actions - Utilisateurs
  loadAllUsers: (filters?: { residenceId?: string; category?: string; status?: string }) => Promise<void>;
  getUserById: (id: string) => Promise<AdminUser | null>;
  updateUserStatus: (userId: string, enabled: boolean) => Promise<boolean>;
  deleteUser: (userId: string) => Promise<boolean>;

  // ✅ CORRIGÉ: Actions - Abonnements (return type inclut 'grace')
  updateSubscription: (residenceId: string, subscription: SubscriptionUpdate) => Promise<boolean>;
  extendSubscription: (residenceId: string, months: number) => Promise<boolean>;
  activateSubscription: (residenceId: string, plan: 'small' | 'medium' | 'large' | 'xlarge', durationMonths: number) => Promise<boolean>;
  cancelSubscription: (residenceId: string, endDate?: string) => Promise<boolean>;
  getExpiringSubscriptions: (daysThreshold?: number) => AdminResidence[];
  getExpiredSubscriptions: () => AdminResidence[];
  getSubscriptionStats: () => { active: number; expiring: number; expired: number; trial: number; grace: number };

  // Reset
  clearError: () => void;
}

export const useAdminStore = create<AdminState>((set, get) => ({
  // État initial
  residences: [],
  syndics: [],
  users: [],
  stats: null,
  isLoadingResidences: false,
  isLoadingSyndics: false,
  isLoadingUsers: false,
  isLoadingStats: false,
  error: null,

  clearError: () => set({ error: null }),

  // ============================================================================
  // STATISTIQUES
  // ============================================================================

  loadStats: async () => {
    set({ isLoadingStats: true, error: null });

    try {
      // Compter les résidences
      const residencesSnap = await getDocs(collection(db, 'residences'));
      const totalResidences = residencesSnap.size;

      // Compter les lots
      let totalLots = 0;
      residencesSnap.forEach(doc => {
        const data = doc.data();
        totalLots += data.stats?.apartments || 0;
      });

      // Compter les syndics actifs
      const syndicsQuery = query(
        collection(db, 'syndics'),
        where('status', '==', 'active')
      );
      const syndicsSnap = await getDocs(syndicsQuery);
      const totalSyndics = syndicsSnap.size;

      // Compter les membres actifs
      const membersQuery = query(
        collection(db, 'members'),
        where('status', '==', 'active')
      );
      const membersSnap = await getDocs(membersQuery);
      const totalMembers = membersSnap.size;

      // Compter les demandes en attente
      let pendingRequests = 0;
      for (const resDoc of residencesSnap.docs) {
        const requestsQuery = query(
          collection(db, 'residences', resDoc.id, 'requests'),
          where('status', '==', 'pending')
        );
        const requestsSnap = await getDocs(requestsQuery);
        pendingRequests += requestsSnap.size;
      }

      // Compter les inscriptions en attente
      const pendingMembersQuery = query(
        collection(db, 'members'),
        where('status', '==', 'pending')
      );
      const pendingMembersSnap = await getDocs(pendingMembersQuery);
      const pendingInscriptions = pendingMembersSnap.size;

      // Nouveaux inscrits ce mois-ci
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);
      
      let newMembersThisMonth = 0;
      membersSnap.forEach(doc => {
        const data = doc.data();
        if (data.createdAt) {
          const createdDate = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
          if (createdDate >= startOfMonth) {
            newMembersThisMonth++;
          }
        }
      });

      set({
        stats: {
          totalResidences,
          totalSyndics,
          totalMembers,
          totalLots,
          pendingRequests,
          pendingInscriptions,
          newMembersThisMonth
        },
        isLoadingStats: false
      });

    } catch (error) {
      console.error('[AdminStore] Error loading stats:', error);
      set({ isLoadingStats: false, error: 'Erreur lors du chargement des statistiques' });
    }
  },

  // ============================================================================
  // RÉSIDENCES
  // ============================================================================

  loadAllResidences: async () => {
    set({ isLoadingResidences: true, error: null });

    try {
      const residencesQuery = query(
        collection(db, 'residences'),
        orderBy('name', 'asc')
      );

      const snapshot = await getDocs(residencesQuery);
      const residences: AdminResidence[] = [];

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();

        // Compter les membres
        const membersQuery = query(
          collection(db, 'members'),
          where('residenceId', '==', docSnap.id)
        );
        const membersSnap = await getDocs(membersQuery);

        // Compter les demandes en attente
        const requestsQuery = query(
          collection(db, 'residences', docSnap.id, 'requests'),
          where('status', '==', 'pending')
        );
        const requestsSnap = await getDocs(requestsQuery);

        residences.push({
          id: docSnap.id,
          name: data.name || '',
          address: data.address || '',
          postalCode: data.postalCode || '',
          city: data.city || '',
          residenceCode: data.residenceCode || '',
          syndicId: data.syndicId || null,
          syndic: normalizeSyndicData(data.syndic),
          stats: data.stats || { apartments: 0, owners: 0, tenants: 0 },
          membersCount: membersSnap.size,
          pendingRequestsCount: requestsSnap.size,
          photo: data.photo,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || '',
          subscription: data.subscription || null,
          planAlert: data.planAlert || null,
          activeUsersCount: data.activeUsersCount ?? null,
          preSeeded: data.preSeeded || false,
        });
      }

      set({ residences, isLoadingResidences: false });

    } catch (error) {
      console.error('[AdminStore] Error loading residences:', error);
      set({ isLoadingResidences: false, error: 'Erreur lors du chargement des résidences' });
    }
  },

  getResidenceById: async (id: string) => {
    try {
      const docSnap = await getDoc(doc(db, 'residences', id));
      if (!docSnap.exists()) return null;

      const data = docSnap.data();
      
      // Compter les membres
      const membersQuery = query(
        collection(db, 'members'),
        where('residenceId', '==', id)
      );
      const membersSnap = await getDocs(membersQuery);

      return {
        id: docSnap.id,
        name: data.name || '',
        address: data.address || '',
        postalCode: data.postalCode || '',
        city: data.city || '',
        residenceCode: data.residenceCode || '',
        syndicId: data.syndicId || null,
        syndic: normalizeSyndicData(data.syndic),
        stats: data.stats || { apartments: 0, owners: 0, tenants: 0 },
        membersCount: membersSnap.size,
        pendingRequestsCount: 0,
        photo: data.photo,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || '',
        subscription: data.subscription || null,
        planAlert: data.planAlert || null,
        activeUsersCount: data.activeUsersCount ?? null,
        preSeeded: data.preSeeded || false,
      };

    } catch (error) {
      console.error('[AdminStore] Error getting residence:', error);
      return null;
    }
  },

  createResidence: async (data) => {
    try {
      // Créer la résidence avec les valeurs par défaut (code temporaire)
      // ✅ createDefaultResidence() crée maintenant correctement un trial de 30 jours
      const residenceData = createDefaultResidence(data);

      // Ajouter les stats si fournies
      if ((data as any).stats) {
        residenceData.stats = (data as any).stats;
      }

      // Nettoyer les champs undefined
      const cleanData = removeUndefinedFields(residenceData);

      const docRef = await addDoc(collection(db, 'residences'), {
        ...cleanData,
        // ✅ La subscription trial est déjà incluse dans cleanData via createDefaultResidence()
        createdAt: Timestamp.now()
      });

      // Générer le code client-facing avec vérification d'unicité Firestore
      const residenceCode = await generateUniqueResidenceCode(data.postalCode, docRef.id);
      await updateDoc(doc(db, 'residences', docRef.id), { residenceCode });

      // Créer les groupes de chat par défaut
      await createAllChatGroups(docRef.id);

      await get().loadAllResidences();

      return docRef.id;

    } catch (error) {
      console.error('[AdminStore] Error creating residence:', error);
      set({ error: 'Erreur lors de la création de la résidence' });
      return null;
    }
  },

  updateResidence: async (id: string, data: Partial<AdminResidence>) => {
    try {
      const cleanData = removeUndefinedFields(data);
      
      await updateDoc(doc(db, 'residences', id), {
        ...cleanData,
        updatedAt: new Date().toISOString()
      });

      await get().loadAllResidences();

      return true;

    } catch (error) {
      console.error('[AdminStore] Error updating residence:', error);
      set({ error: 'Erreur lors de la mise à jour' });
      return false;
    }
  },

  deleteResidence: async (id: string) => {
    try {
      await deleteDoc(doc(db, 'residences', id));

      set(state => ({
        residences: state.residences.filter(r => r.id !== id)
      }));

      return true;

    } catch (error) {
      console.error('[AdminStore] Error deleting residence:', error);
      set({ error: 'Erreur lors de la suppression' });
      return false;
    }
  },

  // ============================================================================
  // SYNDICS
  // ============================================================================

  loadAllSyndics: async () => {
    set({ isLoadingSyndics: true, error: null });

    try {
      const syndicsQuery = query(
        collection(db, 'syndics'),
        orderBy('companyName', 'asc')
      );

      const snapshot = await getDocs(syndicsQuery);
      const syndics: Syndic[] = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          companyName: data.companyName || '',
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || '',
          portalUrl: data.portalUrl || '',
          status: data.status || 'active',
          managedResidenceIds: data.managedResidenceIds || [],
          stats: data.stats || { totalResidences: 0, totalLots: 0, pendingRequests: 0 },
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || '',
          createdBy: data.createdBy || '',
          updatedAt: data.updatedAt,
          // Champs legacy (pour compatibilité)
          siret: data.siret || '',
          contactName: data.contactName || '',
          contactEmail: data.contactEmail || '',
          contactPhone: data.contactPhone || ''
        };
      });

      set({ syndics, isLoadingSyndics: false });

    } catch (error) {
      console.error('[AdminStore] Error loading syndics:', error);
      set({ isLoadingSyndics: false, error: 'Erreur lors du chargement des syndics' });
    }
  },

  getSyndicById: async (id: string) => {
    try {
      const docRef = doc(db, 'syndics', id);
      
      // Essayer d'abord depuis le serveur (important après création via Cloud Function)
      let docSnap;
      try {
        docSnap = await getDocFromServer(docRef);
      } catch (serverError) {
        // Fallback sur le cache si le serveur n'est pas accessible
        logger.db(' Server unavailable, falling back to cache');
        docSnap = await getDoc(docRef);
      }
      
      if (!docSnap.exists()) return null;

      const data = docSnap.data();
      return {
        id: docSnap.id,
        companyName: data.companyName || '',
        address: data.address || '',
        phone: data.phone || '',
        email: data.email || '',
        portalUrl: data.portalUrl || '',
        status: data.status || 'active',
        managedResidenceIds: data.managedResidenceIds || [],
        stats: data.stats || { totalResidences: 0, totalLots: 0, pendingRequests: 0 },
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || '',
        createdBy: data.createdBy || '',
        updatedAt: data.updatedAt,
        // Champs legacy
        siret: data.siret || '',
        contactName: data.contactName || '',
        contactEmail: data.contactEmail || '',
        contactPhone: data.contactPhone || ''
      };

    } catch (error) {
      console.error('[AdminStore] Error getting syndic:', error);
      return null;
    }
  },

  createSyndic: async (data: CreateSyndicData, adminId: string) => {
    try {
      // Appeler la Cloud Function pour créer le syndic
      const createSyndicUser = httpsCallable(functions, 'createSyndicUser');
      
      const result = await createSyndicUser({
        companyName: data.companyName,
        address: data.address || '',
        email: data.email,
        phone: data.phone || '',
        portalUrl: data.portalUrl || '',
        createdBy: adminId || 'system'
      });

      const response = result.data as { success: boolean; syndicId?: string; error?: string };

      if (response.success && response.syndicId) {
        await get().loadAllSyndics();
        return response.syndicId;
      } else {
        set({ error: response.error || 'Erreur lors de la création du syndic' });
        return null;
      }

    } catch (error: any) {
      console.error('[AdminStore] Error creating syndic:', error);
      set({ error: error.message || 'Erreur lors de la création du syndic' });
      return null;
    }
  },

  updateSyndic: async (id: string, data: Partial<Syndic>) => {
    try {
      const cleanData = removeUndefinedFields(data);
      
      await updateDoc(doc(db, 'syndics', id), {
        ...cleanData,
        updatedAt: new Date().toISOString()
      });

      await get().loadAllSyndics();

      return true;

    } catch (error) {
      console.error('[AdminStore] Error updating syndic:', error);
      set({ error: 'Erreur lors de la mise à jour' });
      return false;
    }
  },

  suspendSyndic: async (id: string) => {
    try {
      await updateDoc(doc(db, 'syndics', id), {
        status: 'suspended',
        updatedAt: new Date().toISOString()
      });

      set(state => ({
        syndics: state.syndics.map(s => 
          s.id === id ? { ...s, status: 'suspended' as const } : s
        )
      }));

      return true;

    } catch (error) {
      console.error('[AdminStore] Error suspending syndic:', error);
      set({ error: 'Erreur lors de la suspension' });
      return false;
    }
  },

  revokeSyndic: async (id: string) => {
    try {
      await updateDoc(doc(db, 'syndics', id), {
        status: 'revoked',
        updatedAt: new Date().toISOString()
      });

      set(state => ({
        syndics: state.syndics.map(s => 
          s.id === id ? { ...s, status: 'revoked' as const } : s
        )
      }));

      return true;

    } catch (error) {
      console.error('[AdminStore] Error revoking syndic:', error);
      set({ error: 'Erreur lors de la révocation' });
      return false;
    }
  },

  activateSyndic: async (id: string) => {
    try {
      await updateDoc(doc(db, 'syndics', id), {
        status: 'active',
        updatedAt: new Date().toISOString()
      });

      set(state => ({
        syndics: state.syndics.map(s => 
          s.id === id ? { ...s, status: 'active' as const } : s
        )
      }));

      return true;

    } catch (error) {
      console.error('[AdminStore] Error activating syndic:', error);
      set({ error: 'Erreur lors de l\'activation' });
      return false;
    }
  },

  // ============================================================================
  // MANDATS
  // ============================================================================

  assignResidenceToSyndic: async (residenceId, syndicId, startDate, endDate, adminId) => {
    try {
      const batch = writeBatch(db);

      // 1. Créer le mandat
      const mandateRef = doc(collection(db, 'syndicMandates'));
      batch.set(mandateRef, {
        syndicId,
        residenceId,
        startDate,
        endDate,
        terminatedAt: null,
        status: 'active',
        terminationReason: null,
        transferredToSyndicId: null,
        transferredFromSyndicId: null,
        createdAt: new Date().toISOString(),
        createdBy: adminId || 'system'
      });

      // 2. Mettre à jour la résidence
      const syndicDoc = await getDoc(doc(db, 'syndics', syndicId));
      const syndicData = syndicDoc.data();
      
      batch.update(doc(db, 'residences', residenceId), {
        syndicId,
        syndic: {
          name: syndicData?.companyName || '',
          address: syndicData?.address || '',
          email: syndicData?.contactEmail || syndicData?.email || '',
          phone: syndicData?.contactPhone || syndicData?.phone || '',
          portalUrl: syndicData?.portalUrl || '',
          hours: syndicData?.hours || '',
        },
        updatedAt: new Date().toISOString()
      });

      // 3. Mettre à jour le syndic
      const syndic = await get().getSyndicById(syndicId);
      if (syndic) {
        const currentManagedResidences = syndic.managedResidenceIds || [];
        const newManagedResidences = [...currentManagedResidences, residenceId];
        batch.update(doc(db, 'syndics', syndicId), {
          managedResidenceIds: newManagedResidences,
          'stats.totalResidences': newManagedResidences.length,
          updatedAt: new Date().toISOString()
        });
      }

      await batch.commit();

      await Promise.all([
        get().loadAllResidences(),
        get().loadAllSyndics()
      ]);

      return true;

    } catch (error) {
      console.error('[AdminStore] Error assigning residence:', error);
      set({ error: 'Erreur lors de l\'attribution' });
      return false;
    }
  },

  terminateMandate: async (residenceId, reason, adminId) => {
    try {
      const batch = writeBatch(db);

      // Trouver le mandat actif
      const mandatesQuery = query(
        collection(db, 'syndicMandates'),
        where('residenceId', '==', residenceId),
        where('status', '==', 'active')
      );
      const mandatesSnap = await getDocs(mandatesQuery);
      
      if (mandatesSnap.empty) {
        set({ error: 'Aucun mandat actif trouvé' });
        return false;
      }

      const mandateDoc = mandatesSnap.docs[0];
      const mandateData = mandateDoc.data();
      const syndicId = mandateData.syndicId;

      // 1. Terminer le mandat
      batch.update(mandateDoc.ref, {
        status: 'terminated',
        terminatedAt: new Date().toISOString(),
        terminationReason: reason,
        updatedAt: new Date().toISOString()
      });

      // 2. Retirer le syndic de la résidence
      batch.update(doc(db, 'residences', residenceId), {
        syndicId: null,
        syndic: null,
        updatedAt: new Date().toISOString()
      });

      // 3. Mettre à jour le syndic
      const syndic = await get().getSyndicById(syndicId);
      if (syndic) {
        const currentManagedResidences = syndic.managedResidenceIds || [];
        const newManagedResidences = currentManagedResidences.filter(id => id !== residenceId);
        batch.update(doc(db, 'syndics', syndicId), {
          managedResidenceIds: newManagedResidences,
          'stats.totalResidences': newManagedResidences.length,
          updatedAt: new Date().toISOString()
        });
      }

      await batch.commit();

      await Promise.all([
        get().loadAllResidences(),
        get().loadAllSyndics()
      ]);

      return true;

    } catch (error) {
      console.error('[AdminStore] Error terminating mandate:', error);
      set({ error: 'Erreur lors de la résiliation' });
      return false;
    }
  },

  transferResidence: async (residenceId, toSyndicId, transferDate, reason, adminId) => {
    try {
      // Vérifier d'abord s'il y a un mandat actif
      const mandatesQuery = query(
        collection(db, 'syndicMandates'),
        where('residenceId', '==', residenceId),
        where('status', '==', 'active')
      );
      const mandatesSnap = await getDocs(mandatesQuery);
      
      // S'il y a un mandat actif, le terminer d'abord
      if (!mandatesSnap.empty) {
        const terminated = await get().terminateMandate(residenceId, 'transferred', adminId);
        if (!terminated) return false;
      }

      // Puis assigner au nouveau syndic
      const assigned = await get().assignResidenceToSyndic(
        residenceId, 
        toSyndicId, 
        transferDate, 
        null, 
        adminId
      );

      return assigned;

    } catch (error) {
      console.error('[AdminStore] Error transferring residence:', error);
      set({ error: 'Erreur lors du transfert' });
      return false;
    }
  },

  // ============================================================================
  // UTILISATEURS
  // ============================================================================

  loadAllUsers: async (filters) => {
    set({ isLoadingUsers: true, error: null });

    try {
      let usersQuery = query(collection(db, 'members'), orderBy('name'));

      const usersSnap = await getDocs(usersQuery);
      let users: AdminUser[] = [];

      // Charger les noms des résidences
      const residencesSnap = await getDocs(collection(db, 'residences'));
      const residenceNames: Record<string, string> = {};
      residencesSnap.forEach(doc => {
        residenceNames[doc.id] = doc.data().name || '';
      });

      // Charger les noms des syndics
      const syndicsSnap = await getDocs(collection(db, 'syndics'));
      const syndicNames: Record<string, string> = {};
      syndicsSnap.forEach(doc => {
        syndicNames[doc.id] = doc.data().companyName || '';
      });

      const filteredDocs = usersSnap.docs.filter(doc => {
        const data = doc.data();
        if (filters?.residenceId && data.residenceId !== filters.residenceId) return false;
        if (filters?.category && data.category !== filters.category) return false;
        if (filters?.status && data.status !== filters.status) return false;
        return true;
      });

      const privateMap = await batchFetchMemberPrivate(filteredDocs.map(d => d.id));

      filteredDocs.forEach(docSnap => {
        const data = docSnap.data();
        const p = privateMap[docSnap.id] || data;
        users.push({
          id: docSnap.id,
          name: data.name || '',
          email: p.email || '',
          category: data.category || 'tenant',
          residenceId: data.residenceId || null,
          residenceName: data.residenceId ? residenceNames[data.residenceId] : undefined,
          apartment: p.apartment || '',
          status: data.status || 'active',
          enabled: data.enabled !== false,
          syndicId: data.syndicId || undefined,
          syndicName: data.syndicId ? syndicNames[data.syndicId] : undefined,
          createdAt: p.createdAt || data.createdAt || ''
        });
      });

      set({ users, isLoadingUsers: false });

    } catch (error) {
      console.error('[AdminStore] Error loading users:', error);
      set({ isLoadingUsers: false, error: 'Erreur lors du chargement des utilisateurs' });
    }
  },

  getUserById: async (id: string) => {
    try {
      const [userDoc, privateDoc] = await Promise.all([
        getDoc(doc(db, 'members', id)),
        getDoc(doc(db, 'memberPrivate', id)),
      ]);
      if (!userDoc.exists()) return null;

      const data = userDoc.data();
      const p = privateDoc.exists() ? privateDoc.data() : data;

      // Charger le nom de la résidence
      let residenceName: string | undefined;
      if (data.residenceId) {
        const resDoc = await getDoc(doc(db, 'residences', data.residenceId));
        if (resDoc.exists()) {
          residenceName = resDoc.data().name;
        }
      }

      return {
        id: userDoc.id,
        name: data.name || '',
        email: p.email || '',
        category: data.category || 'tenant',
        residenceId: data.residenceId || null,
        residenceName,
        apartment: p.apartment || '',
        status: data.status || 'active',
        enabled: data.enabled !== false,
        syndicId: data.syndicId || undefined,
        createdAt: p.createdAt || data.createdAt || ''
      };

    } catch (error) {
      console.error('[AdminStore] Error getting user:', error);
      return null;
    }
  },

  updateUserStatus: async (userId: string, enabled: boolean) => {
    try {
      await updateDoc(doc(db, 'members', userId), {
        enabled,
        updatedAt: new Date().toISOString()
      });

      set(state => ({
        users: state.users.map(u => 
          u.id === userId ? { ...u, enabled } : u
        )
      }));

      return true;

    } catch (error) {
      console.error('[AdminStore] Error updating user status:', error);
      set({ error: 'Erreur lors de la mise à jour' });
      return false;
    }
  },

  deleteUser: async (userId: string) => {
    try {
      await deleteDoc(doc(db, 'members', userId));

      set(state => ({
        users: state.users.filter(u => u.id !== userId)
      }));

      return true;

    } catch (error) {
      console.error('[AdminStore] Error deleting user:', error);
      set({ error: 'Erreur lors de la suppression' });
      return false;
    }
  },

  // ============================================================================
  // ABONNEMENTS
  // ✅ CORRIGÉ: Toutes les méthodes supportent le statut 'grace'
  // ============================================================================

  updateSubscription: async (residenceId: string, subscriptionUpdate: SubscriptionUpdate) => {
    try {
      const residenceRef = doc(db, 'residences', residenceId);
      
      // Construire l'objet de mise à jour
      const updates: Record<string, any> = {
        updatedAt: new Date().toISOString()
      };
      
      if (subscriptionUpdate.status !== undefined) {
        updates['subscription.status'] = subscriptionUpdate.status;
        updates['subscription.statusUpdatedAt'] = new Date().toISOString();
      }
      if (subscriptionUpdate.endDate !== undefined) {
        updates['subscription.endDate'] = subscriptionUpdate.endDate;
      }
      if (subscriptionUpdate.plan !== undefined) {
        updates['subscription.plan'] = subscriptionUpdate.plan;
      }
      if (subscriptionUpdate.autoRenew !== undefined) {
        updates['subscription.autoRenew'] = subscriptionUpdate.autoRenew;
      }

      await updateDoc(residenceRef, updates);

      // Mettre à jour le state local
      set(state => ({
        residences: state.residences.map(r => 
          r.id === residenceId 
            ? { ...r, subscription: { ...r.subscription, ...subscriptionUpdate } as any }
            : r
        )
      }));

      return true;

    } catch (error) {
      console.error('[AdminStore] Error updating subscription:', error);
      set({ error: 'Erreur lors de la mise à jour de l\'abonnement' });
      return false;
    }
  },

  extendSubscription: async (residenceId: string, months: number) => {
    try {
      const { residences } = get();
      const residence = residences.find(r => r.id === residenceId);
      
      if (!residence) {
        set({ error: 'Résidence non trouvée' });
        return false;
      }

      // Calculer la nouvelle date de fin
      const currentEndDate = residence.subscription?.endDate 
        ? new Date(residence.subscription.endDate)
        : new Date();
      
      // Si la date actuelle est passée, partir d'aujourd'hui
      const baseDate = currentEndDate > new Date() ? currentEndDate : new Date();
      const newEndDate = new Date(baseDate);
      newEndDate.setMonth(newEndDate.getMonth() + months);

      const residenceRef = doc(db, 'residences', residenceId);
      
      // ✅ Nettoyer graceStartDate lors de l'extension (on sort de la grâce)
      await updateDoc(residenceRef, {
        'subscription.status': 'active',
        'subscription.endDate': newEndDate.toISOString(),
        'subscription.statusUpdatedAt': new Date().toISOString(),
        'subscription.graceStartDate': deleteField(),  // ✅ Supprimer le champ grâce
        updatedAt: new Date().toISOString()
      });

      // Mettre à jour le state local
      set(state => ({
        residences: state.residences.map(r => 
          r.id === residenceId 
            ? { 
                ...r, 
                subscription: { 
                  ...r.subscription, 
                  status: 'active' as const,
                  endDate: newEndDate.toISOString(),
                  graceStartDate: undefined  // ✅ Nettoyé localement aussi
                } as any
              }
            : r
        )
      }));

      return true;

    } catch (error) {
      console.error('[AdminStore] Error extending subscription:', error);
      set({ error: 'Erreur lors de la prolongation de l\'abonnement' });
      return false;
    }
  },

  activateSubscription: async (residenceId: string, plan: 'small' | 'medium' | 'large' | 'xlarge', durationMonths: number) => {
    try {
      const startDate = new Date();
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + durationMonths);

      const subscription = {
        status: 'active' as const,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        plan,
        autoRenew: false,
        statusUpdatedAt: new Date().toISOString()
        // ✅ graceStartDate n'est PAS inclus → sera supprimé par l'écrasement complet
      };

      const residenceRef = doc(db, 'residences', residenceId);
      
      // ✅ Écriture complète de l'objet subscription (écrase tout, y compris graceStartDate)
      await updateDoc(residenceRef, {
        subscription,
        updatedAt: new Date().toISOString()
      });

      // Mettre à jour le state local
      set(state => ({
        residences: state.residences.map(r => 
          r.id === residenceId 
            ? { ...r, subscription }
            : r
        )
      }));

      return true;

    } catch (error) {
      console.error('[AdminStore] Error activating subscription:', error);
      set({ error: 'Erreur lors de l\'activation de l\'abonnement' });
      return false;
    }
  },

  cancelSubscription: async (residenceId: string, endDate?: string) => {
    try {
      const residenceRef = doc(db, 'residences', residenceId);
      const now = new Date().toISOString();

      // Synchroniser avec Stripe si un abonnement actif existe (ne bloque pas en cas d'échec)
      const residenceSnap = await getDoc(residenceRef);
      const stripeSubscriptionId = residenceSnap.data()?.subscription?.stripeSubscriptionId;
      if (stripeSubscriptionId) {
        try {
          const cancelStripeSubscription = httpsCallable(functions, 'cancelStripeSubscription');
          await cancelStripeSubscription({ residenceId });
        } catch (stripeErr) {
          console.warn('[AdminStore] cancelStripeSubscription Cloud Function échouée (Firestore OK):', stripeErr);
        }
      }

      if (endDate) {
        // Résiliation différée : accès maintenu jusqu'à endDate
        const daysRemaining = Math.ceil(
          (new Date(endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
        );
        const status = daysRemaining > 30 ? 'active' : daysRemaining > 0 ? 'expiring' : 'grace';

        await updateDoc(residenceRef, {
          'subscription.endDate': endDate,
          'subscription.status': status,
          'subscription.autoRenew': false,
          'subscription.statusUpdatedAt': now,
          updatedAt: now
        });

        set(state => ({
          residences: state.residences.map(r =>
            r.id === residenceId
              ? { ...r, subscription: { ...r.subscription, endDate, status, autoRenew: false } as any }
              : r
          )
        }));
      } else {
        // Résiliation immédiate → période de grâce
        await updateDoc(residenceRef, {
          'subscription.status': 'grace',
          'subscription.graceStartDate': now,
          'subscription.autoRenew': false,
          'subscription.statusUpdatedAt': now,
          updatedAt: now
        });

        set(state => ({
          residences: state.residences.map(r =>
            r.id === residenceId
              ? { ...r, subscription: { ...r.subscription, status: 'grace' as const, graceStartDate: now, autoRenew: false } as any }
              : r
          )
        }));
      }

      return true;
    } catch (error) {
      console.error('[AdminStore] Error cancelling subscription:', error);
      set({ error: 'Erreur lors de l\'annulation de l\'abonnement' });
      return false;
    }
  },

  getExpiringSubscriptions: (daysThreshold: number = 30) => {
    const { residences } = get();
    const now = new Date();
    const thresholdDate = new Date();
    thresholdDate.setDate(thresholdDate.getDate() + daysThreshold);

    return residences.filter(r => {
      if (!r.subscription?.endDate) return false;
      const endDate = new Date(r.subscription.endDate);
      return endDate > now && endDate <= thresholdDate 
        && r.subscription.status !== 'expired' 
        && r.subscription.status !== 'grace';  // ✅ Exclure grace
    });
  },

  getExpiredSubscriptions: () => {
    const { residences } = get();

    return residences.filter(r => {
      if (!r.subscription?.endDate) return false;
      // ✅ Inclure grace et expired
      return r.subscription.status === 'expired' || r.subscription.status === 'grace';
    });
  },

  // ✅ CORRIGÉ: getSubscriptionStats inclut le compteur 'grace'
  getSubscriptionStats: () => {
    const { residences } = get();
    const now = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    let active = 0;
    let expiring = 0;
    let expired = 0;
    let trial = 0;
    let grace = 0;

    residences.forEach(r => {
      if (!r.subscription) {
        // Pas d'abonnement = considéré comme trial
        trial++;
        return;
      }

      // ✅ Utiliser le statut Firestore comme source de vérité
      switch (r.subscription.status) {
        case 'trial':
          trial++;
          break;
        case 'grace':
          grace++;
          break;
        case 'expired':
          expired++;
          break;
        case 'expiring':
          expiring++;
          break;
        case 'active': {
          // Vérifier par date si le cron n'a pas encore tourné
          const endDate = new Date(r.subscription.endDate);
          if (endDate < now) {
            grace++; // Devrait être en grâce
          } else if (endDate <= thirtyDaysFromNow) {
            expiring++;
          } else {
            active++;
          }
          break;
        }
        default:
          active++;
      }
    });

    return { active, expiring, expired, trial, grace };
  }
}));

export default useAdminStore;
